import { hasTrackingConsent } from '../consent/trackingConsent';
import { readStoredAttribution } from './attributionStore';

/** The `attribution` input of `auth.verifyCode` and `auth.socialSignIn`. */
export interface SignupAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  ref?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  fbclid?: string;
  msclkid?: string;
  referrer?: string;
  landingPage?: string;
}

const clamp = (value: string | undefined, max: number): string | undefined =>
  value === undefined || value === '' ? undefined : value.substring(0, max);

/**
 * The stored touch as the sign-in input, or `undefined` when there is none. The campaign
 * parameters name a campaign, not a person, so they always go. The click ids, the referrer and the
 * landing page describe one person's click: they go only after Accept in the consent bar.
 */
export const getSignupAttribution = (): SignupAttribution | undefined => {
  const stored = readStoredAttribution();
  if (stored === undefined) {
    return undefined;
  }
  const { v } = stored;
  const consented = hasTrackingConsent();
  const attribution: SignupAttribution = {
    utmSource: clamp(v.utm_source, 255),
    utmMedium: clamp(v.utm_medium, 255),
    utmCampaign: clamp(v.utm_campaign, 255),
    utmContent: clamp(v.utm_content, 255),
    utmTerm: clamp(v.utm_term, 255),
    ref: clamp(v.ref, 255),
    gclid: consented ? clamp(v.gclid, 512) : undefined,
    wbraid: consented ? clamp(v.wbraid, 512) : undefined,
    gbraid: consented ? clamp(v.gbraid, 512) : undefined,
    fbclid: consented ? clamp(v.fbclid, 512) : undefined,
    msclkid: consented ? clamp(v.msclkid, 512) : undefined,
    referrer: consented ? clamp(stored.r, 1024) : undefined,
    landingPage: consented ? clamp(stored.lp, 1024) : undefined,
  };
  const cleaned = Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => value !== undefined)
  ) as SignupAttribution;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};
